'use client';
import { useEffect, useState } from 'react';
import { isNullish } from '@witmetrics/utils/misc';
import { useMountEffect } from '@/hooks/useMountEffect';
import type { StorageType } from './types';
import { getInitialValue, getType, parseValue, stringifyValue } from './utils';

export function useStorage<T>(
  storage: StorageType,
  key: string,
  initialValue: T
) {
  const [value, setValue] = useState<T>(initialValue);

  useMountEffect(() => {
    try {
      if (
        window[storage].getItem(key) !== null &&
        (initialValue === null || initialValue === undefined)
      ) {
        const storedValue = JSON.parse(window[storage].getItem(key)!);
        const parsedValue = parseValue(storedValue.type, storedValue.value);
        setValue(parsedValue);
      }
    } catch (err) {
      console.error(err);
    }
  });

  useEffect(() => {
    if (!isNullish(value)) storeValue();
  }, [value]);

  const storeValue = () => {
    const type = getType(value);
    try {
      window[storage].setItem(
        key,
        JSON.stringify({ type, value: stringifyValue(type, value) })
      );
    } catch (err) {
      console.error(err);
    }
  };

  const clearValue = () => {
    try {
      window[storage].removeItem(key);
    } catch (err) {
      console.error(err);
    }
  };

  return [value, setValue, { clearValue }] as const;
}

export function useLocalStorage<T>(
  key: string,
  initialValue: T,
  useExisting = false
) {
  return useStorage(
    'localStorage',
    key,
    getInitialValue('localStorage', key, initialValue, useExisting)
  );
}

export function useSessionStorage<T>(
  key: string,
  initialValue: T,
  useExisting = false
) {
  return useStorage(
    'sessionStorage',
    key,
    getInitialValue('sessionStorage', key, initialValue, useExisting)
  );
}
